// Token-level column-ref scanners — Slice 18 (`emitAliasQualifiedColumnRefs`)
// + Slice 0.2 (`emitBareColumnRefs`).
//
// Both scanners walk the body source and emit `column` reference records into
// the supplied IdentifierIndex. They share a mutable `alreadyEmitted: Set<string>`
// keyed by `<foldedName>::<startOffset>` so a ref one scanner already emitted
// is not re-recorded by the other.
//
// `emitAliasQualifiedColumnRefs` (Slice 18 fallback) covers the
// `<alias>.<column>` pattern. The structural extractor's select-list splitter
// trips on engine-specific quoted identifiers (e.g. MySQL backticked aliases),
// so this scanner walks the body independently and re-emits column refs the
// structural walker missed.
//
// `emitBareColumnRefs` (Slice 0.2) covers bare `<column>` references inside
// opaque-text clause regions (WHERE / HAVING / ORDER BY) — only when the FROM
// scope contains exactly one relation. Multi-table scopes are ambiguous and
// are skipped entirely; recording bare refs there would produce false-positive
// find-references hits.

import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions, QualifiedName, SqlRange } from '../../sql-grammar/model';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../../sql-resolver/classify/tokenSkip';
import { readQualifiedName, type ReadQualifiedNameResult } from '../../sql-resolver/scope/identifiers';
import type { QueryStructure } from '../../sql-resolver/types';

import type { IdentifierIndex, ReferenceRecord } from '../types';

import type { PersistedObjectListing } from './objectBodyFetcher';

/** Walk the body source for `<alias>.<column>` patterns where `<alias>` is a
 *  known FROM/JOIN alias in `qs`. Emits one column ReferenceRecord per match.
 *  Idempotent: dedupe by `(foldedName, startOffset)` so we don't double-record
 *  refs the structural extractor already surfaced via `selectItems[*].resolved`.
 *  After emission the dedupe key is added to `alreadyEmitted` so a subsequent
 *  `emitBareColumnRefs` call sees the same key and skips a co-located bare ref. */
export function emitAliasQualifiedColumnRefs(
  qs: QueryStructure,
  source: string,
  options: ParseOptions,
  listing: PersistedObjectListing,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
  alreadyEmitted: Set<string>,
): void {
  const aliasTargets = collectAliasTargets(qs);
  if (aliasTargets.size === 0) return;
  scanIdents(source, 0, source.length, options, (qn) => {
    const folded = qn.qualified.foldedSchema;
    if (!folded || !qn.qualified.foldedName) return;
    const hostTable = aliasTargets.get(folded.toLowerCase());
    if (!hostTable) return;
    emitColumnRecord(
      qn.qualified,
      qn.range,
      byteOffset,
      lineIdx,
      listing,
      index,
      alreadyEmitted,
      hostTable,
    );
  });
}

/** Walk identifier tokens inside opaque-text clause regions (WHERE / HAVING /
 *  ORDER BY) and emit bare-column refs when the FROM scope is single-relation.
 *  Function-call identifiers (token followed by `(`) and alias-qualified
 *  references (`<alias>.<col>`) are skipped — those land via other paths. */
export function emitBareColumnRefs(
  qs: QueryStructure,
  source: string,
  options: ParseOptions,
  listing: PersistedObjectListing,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
  alreadyEmitted: Set<string>,
): void {
  if (!isSingleRelationScope(qs)) return;
  // Single-relation scope (asserted above) → the only FROM item is a relation;
  // every bare ref in WHERE/HAVING/ORDER BY resolves to that one table.
  const target = qs.fromItems![0].target;
  const hostTable = target.kind === 'relation' ? target.qualified : undefined;
  for (const range of collectOpaqueRegions(qs)) {
    scanIdents(source, range.startOffset, range.endOffset, options, (qn, regionEnd) => {
      if (qn.qualified.foldedSchema) return;
      if (!qn.qualified.foldedName) return;
      if (followedByOpenParen(source, qn.range.endOffset, regionEnd)) return;
      emitColumnRecord(
        qn.qualified,
        qn.range,
        byteOffset,
        lineIdx,
        listing,
        index,
        alreadyEmitted,
        hostTable,
      );
    });
  }
}

/** True iff the QueryStructure's FROM scope contains exactly one relation
 *  FROM-item (with no joins, no compound branches, no CTEs/derived/function
 *  targets). Bare-identifier emission is gated on this — multi-table scopes
 *  are ambiguous and would yield false-positive find-references hits. */
function isSingleRelationScope(qs: QueryStructure): boolean {
  if (qs.compoundBranches && qs.compoundBranches.length > 0) return false;
  if (qs.joins && qs.joins.length > 0) return false;
  if (!qs.fromItems || qs.fromItems.length !== 1) return false;
  return qs.fromItems[0].target.kind === 'relation';
}

function collectOpaqueRegions(qs: QueryStructure): SqlRange[] {
  const out: SqlRange[] = [];
  if (qs.where) for (const f of qs.where) out.push(f.range);
  if (qs.having) for (const f of qs.having) out.push(f.range);
  if (qs.orderBy) for (const o of qs.orderBy) out.push(o.range);
  return out;
}

function followedByOpenParen(source: string, from: number, end: number): boolean {
  let j = from;
  while (j < end && (source[j] === ' ' || source[j] === '\t' || source[j] === '\n' || source[j] === '\r')) j++;
  return source[j] === '(';
}

/** Iterate identifier tokens in `source[start, end)`, invoking `onIdent` for
 *  each `readQualifiedName` hit. Skips comments, single-quoted strings, and
 *  dollar-quoted bodies. Backtick / bracket regions stay in-scope so quoted
 *  identifiers remain reachable. */
function scanIdents(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
  onIdent: (qn: ReadQualifiedNameResult, regionEnd: number) => void,
): void {
  const skipCtx: SkipContext = {
    ...skipContextFor(options),
    supportsBacktick: false,
    supportsBracket: false,
  };
  let i = start;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === ',' || c === '(' || c === ')' || c === '=') {
      i += 1;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, skipCtx);
    if (skipped >= 0 && skipped > i) {
      i = skipped;
      continue;
    }
    const qn = readQualifiedName(source, i, end, options);
    if (!qn) {
      i += 1;
      continue;
    }
    onIdent(qn, end);
    i = qn.range.endOffset > i ? qn.range.endOffset : i + 1;
  }
}

function emitColumnRecord(
  qualified: QualifiedName,
  rawRange: SqlRange,
  byteOffset: number,
  lineIdx: LineIndex,
  listing: PersistedObjectListing,
  index: IdentifierIndex,
  alreadyEmitted: Set<string>,
  hostTable?: QualifiedName,
): void {
  const colStart = rawRange.startOffset + byteOffset;
  const dedupeKey = `${(qualified.foldedName ?? '').toLowerCase()}::${colStart}`;
  if (alreadyEmitted.has(dedupeKey)) return;
  const record: ReferenceRecord = {
    objectKind: listing.objectKind,
    objectName: listing.objectName,
    identifier: canonicalize(qualified),
    identifierKind: 'column',
    range: rangeFromOffsets(lineIdx, rawRange.startOffset + byteOffset, rawRange.endOffset + byteOffset),
    referenceKind: 'read',
    parseStatus: 'ok',
    ...(hostTable ? { hostTable } : {}),
  };
  index.record(record);
  alreadyEmitted.add(dedupeKey);
}

/** Map folded alias → the relation it names, so an `<alias>.<column>` ref can
 *  record the table it resolves to (NAV-1 cross-table disambiguation). Aliases
 *  bound to non-relation targets (derived tables, CTEs, function calls) are
 *  omitted — those refs stay `hostTable: undefined` and fall back to the
 *  planner's v1 "rewrite + user reviews preview" behaviour. */
function collectAliasTargets(qs: QueryStructure): Map<string, QualifiedName> {
  const out = new Map<string, QualifiedName>();
  if (qs.fromItems) {
    for (const f of qs.fromItems) {
      if (f.alias?.aliasFolded && f.target.kind === 'relation') {
        out.set(f.alias.aliasFolded.toLowerCase(), f.target.qualified);
      }
    }
  }
  if (qs.joins) {
    for (const j of qs.joins) {
      if (j.right.alias?.aliasFolded && j.right.target.kind === 'relation') {
        out.set(j.right.alias.aliasFolded.toLowerCase(), j.right.target.qualified);
      }
    }
  }
  if (qs.compoundBranches) {
    for (const branch of qs.compoundBranches) {
      for (const [alias, tbl] of collectAliasTargets(branch)) {
        if (!out.has(alias)) out.set(alias, tbl);
      }
    }
  }
  return out;
}

/** Strip every qualifier before recording a column ref. A column's prefix in a
 *  body is an ALIAS (`u.email`), buffer-scoped noise that would split the index
 *  across every alias spelling — so the index keys all `email` refs under one
 *  bare bucket (mirrors `viewWalker.ts`'s `canonicalizeColumn` and
 *  `constraintExpressionScanner.ts`'s `canonicalize`).
 *
 *  The READERS are qualified (`go-to/columnAtCursor.ts` returns the column plus
 *  its host relation's schema and database), so `index/identifierIndex.ts`'s
 *  `identifierKey` drops the qualification on the lookup side too — otherwise
 *  no cursor-driven rename or find-references ever matches a column. The
 *  cross-table disambiguation those readers need is carried by `hostTable`
 *  below, never by the key. */
function canonicalize(qn: QualifiedName): QualifiedName {
  return {
    name: qn.name,
    raw: qn.name,
    quoted: qn.quoted,
    foldedName: qn.foldedName,
  };
}
